import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, History, User, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";

// Compact "5m ago" style time — copied from Arrivals.jsx to keep the modal
// self-contained (no circular import) and identical to the pickup chip.
const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 90) return `${d}d ago`;
  return iso.slice(0, 10);
};

const roleTone = {
  FOUNDER: "bg-brand text-white",
  BDOM: "bg-blue-600 text-white",
  GEHM: "bg-amber-500 text-white",
  PCWM: "bg-purple-600 text-white",
  SMCM: "bg-slate-600 text-white",
};

export default function ArrivalHistoryModal({ arrival, onClose }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!arrival?.id) return;
    let cancelled = false;
    api.get(`/arrivals/${arrival.id}/history`)
      .then(({ data }) => { if (!cancelled) setRows(data || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [arrival?.id]);

  if (!arrival) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         data-testid="arr-history-modal"
         onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-[color:var(--aii-border)] shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-brand flex items-center gap-1">
              <History size={11}/> Row history
            </div>
            <div className="font-display font-semibold text-lg mt-0.5">{arrival.customer}</div>
            <div className="text-xs text-[color:var(--aii-muted)]">
              {arrival.destination}{arrival.city ? ` · ${arrival.city}` : ""}
              {arrival.flight_date ? ` · flight ${arrival.flight_date}${arrival.flight_time ? " " + arrival.flight_time : ""}` : ""}
            </div>
          </div>
          <button data-testid="arr-history-close" onClick={onClose}
                  className="text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text-1)] p-1">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {rows === null && (
            <div className="text-sm text-[color:var(--aii-muted)] text-center py-8">Loading timeline…</div>
          )}
          {rows && rows.length === 0 && (
            <div className="text-sm text-[color:var(--aii-muted)] text-center py-10 flex flex-col items-center gap-2">
              <Sparkles size={18} className="text-brand"/>
              No history yet for this arrival. Any edit to flight, hotel, notes or pickup will land here.
            </div>
          )}
          {rows && rows.length > 0 && (
            <ol className="relative border-l-2 border-[color:var(--aii-border)] ml-3 space-y-4">
              {rows.map((h) => (
                <li key={h.id} data-testid={`arr-history-entry-${h.id}`} className="ml-4 relative">
                  <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-brand ring-2 ring-white"/>
                  <div className="flex flex-wrap items-baseline gap-2 mb-1">
                    <span className="font-semibold text-sm flex items-center gap-1">
                      <User size={11} className="text-[color:var(--aii-muted)]"/> {h.user_name || "System"}
                    </span>
                    {h.user_role && (
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${roleTone[h.user_role] || "bg-slate-300 text-slate-900"}`}>
                        {h.user_role}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-bold">
                      {h.action === "create" ? "created" : "edited"}
                    </span>
                    <span className="text-[11px] text-[color:var(--aii-muted)]"
                          title={h.at ? new Date(h.at).toLocaleString() : ""}>
                      · {relTime(h.at)}
                    </span>
                  </div>
                  <div className="space-y-1 pl-1">
                    {(h.changes || []).length === 0 && h.action === "create" && (
                      <div className="text-xs text-[color:var(--aii-muted)]">Row created (no field values yet)</div>
                    )}
                    {(h.changes || []).map((c, i) => (
                      <div key={`${h.id}-${c.field}-${i}`} className="text-xs bg-slate-50 border border-[color:var(--aii-border)] rounded px-2 py-1.5">
                        <div className="text-[9px] uppercase tracking-widest font-bold text-brand mb-0.5">{c.label}</div>
                        {h.action === "create" ? (
                          <div className="font-mono text-[color:var(--aii-text-1)]">{c.to || <em className="not-italic text-[color:var(--aii-muted)]">empty</em>}</div>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-wrap font-mono">
                            <span className="line-through text-[color:var(--aii-muted)]">{c.from || <em className="not-italic">empty</em>}</span>
                            <ArrowRight size={10} className="text-brand shrink-0"/>
                            <span className="text-[color:var(--aii-text-1)] font-semibold">{c.to || <em className="not-italic text-[color:var(--aii-muted)]">empty</em>}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
